import { useCallback, useState } from 'react';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';

import { TotalModeEnum } from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { SplitButton } from '../../components/SplitButton';
import { useIsPricingEditable } from '../../contexts/PricingEditionContext/hooks/useIsPricingEditable.hook';
import type { SplitButtonOption } from '../../components/SplitButton';
import type { ResolvableFieldSettings } from '../../components/ResolvableFields/types/resolvable-field-settings.type';
import type { ServiceUsagesTotalCardCmp_Total_Fragment } from '../../components/ServiceUsages/TotalCard/Result';

import type { ManualTotalForm_ResolvableServiceValue_Fragment } from './fragments/manual-total-value.fragment';
import { ManualTotalDialogForm } from '.';
import { ManualTotalDrawerForm } from './ManualTotalDrawerForm';
import {
  useManualTotalInitialValueFromAutomaticResult,
  useManualTotalInitialValueFromManualTotal,
} from './hooks/useManualTotalInitialValues.hook';
import { useSwitchTotalModeSubmit } from './hooks/useSwitchTotalModeSubmit.hook';
import { useUpdateManualTotalSubmit } from './hooks/useUpdateManualTotalSubmit.hook';
import type {
  SwitchTotalModeMutation,
  UpdateManualTotalMutation,
} from './types/props.type';

type FormVariant = 'edit' | 'switchToAutomatic' | 'switchToManual';

const EDIT_ACTION_KEY = 'edit';

const MANUAL_MODE_ACTIONS: readonly SplitButtonOption[] = [
  { icon: <EditIcon />, key: EDIT_ACTION_KEY, label: 'Modifier les valeurs' },
  {
    icon: <SyncIcon />,
    key: 'switchToAutomatic',
    label: 'Passer en mode automatique',
  },
];

interface Props {
  readonly automaticResult:
    | FragmentType<typeof ServiceUsagesTotalCardCmp_Total_Fragment>
    | undefined
    | null;
  readonly manualTotal:
    | FragmentType<typeof ManualTotalForm_ResolvableServiceValue_Fragment>
    | undefined
    | null;
  readonly queryKeysToInvalidate: readonly (readonly string[])[];
  readonly resolvableFieldSettings: ResolvableFieldSettings;
  readonly serviceUsagesGroupId: Id;
  readonly serviceUserId: Id;
  readonly switchTotalModeMutation: SwitchTotalModeMutation;
  readonly totalMode: TotalModeEnum | null | undefined;
  readonly updateManualTotalMutation?: UpdateManualTotalMutation;
}

export const TotalModeSwitch = ({
  automaticResult,
  manualTotal,
  queryKeysToInvalidate,
  resolvableFieldSettings,
  serviceUsagesGroupId,
  serviceUserId,
  switchTotalModeMutation,
  totalMode,
  updateManualTotalMutation,
}: Readonly<Props>) => {
  const isPricingEditable = useIsPricingEditable();

  const [formVariant, setFormVariant] = useState<FormVariant | null>(null);

  const closeForm = useCallback(() => {
    setFormVariant(null);
  }, []);

  const startSwitchToAutomatic = useCallback(() => {
    setFormVariant('switchToAutomatic');
  }, []);

  const startSwitchToManual = useCallback(() => {
    setFormVariant('switchToManual');
  }, []);

  const startEdit = useCallback(() => {
    setFormVariant('edit');
  }, []);

  const initialValueFromAutomaticResult =
    useManualTotalInitialValueFromAutomaticResult(automaticResult);

  const initialValueFromManualTotal =
    useManualTotalInitialValueFromManualTotal(manualTotal);

  const { handleSubmitFactory } = useSwitchTotalModeSubmit({
    mutation: switchTotalModeMutation,
    onSuccess: closeForm,
    queryKeysToInvalidate,
    serviceUsagesGroupId,
    serviceUserId,
  });

  const { handleSubmit: handleUpdateSubmit } = useUpdateManualTotalSubmit({
    mutation: updateManualTotalMutation ?? (() => undefined),
    onSuccess: closeForm,
    queryKeysToInvalidate,
    serviceUsagesGroupId,
    serviceUserId,
  });

  const isManualMode = totalMode === TotalModeEnum.Manual;

  const handleManualModeAction = useCallback(
    ({ key }: Readonly<SplitButtonOption>) => {
      if (key === EDIT_ACTION_KEY) {
        startEdit();

        return;
      }

      startSwitchToAutomatic();
    },
    [startEdit, startSwitchToAutomatic],
  );

  return (
    <>
      <Stack direction="row" sx={{ justifyContent: 'center', mt: 1 }}>
        {isManualMode && updateManualTotalMutation ? (
          <SplitButton
            ariaLabelButton="Autres actions sur le total"
            ariaLabelButtonGroup="Actions sur le total manuel"
            disabled={!isPricingEditable}
            id={`total-mode-actions-${serviceUsagesGroupId.toString()}`}
            onClick={handleManualModeAction}
            options={MANUAL_MODE_ACTIONS}
            size="medium"
          />
        ) : (
          <Button
            disabled={!isPricingEditable}
            onClick={
              isManualMode ? startSwitchToAutomatic : startSwitchToManual
            }
            startIcon={<SyncIcon />}
            variant="contained"
          >
            {isManualMode
              ? 'Passer en mode automatique'
              : 'Passer en mode manuel'}
          </Button>
        )}
      </Stack>
      <ManualTotalDrawerForm
        description="Le total ne sera plus recalculé automatiquement à partir des prestations : renseignez les valeurs à utiliser."
        initialValue={initialValueFromAutomaticResult}
        isOpen={formVariant === 'switchToManual'}
        onClose={closeForm}
        onSubmit={handleSubmitFactory(TotalModeEnum.Manual)}
        resolvableFieldSettings={resolvableFieldSettings}
        submitLabel="Valider"
        title="Passer en mode manuel"
      />
      {formVariant === 'switchToAutomatic' && (
        <ManualTotalDialogForm
          description="Le total sera de nouveau calculé automatiquement à partir des prestations."
          initialValue={
            initialValueFromManualTotal ?? initialValueFromAutomaticResult
          }
          isDialogOpen
          onCloseDialog={closeForm}
          onSubmit={handleSubmitFactory(TotalModeEnum.Automatic)}
          submitLabel="Passer en mode automatique"
          title="Passer en mode automatique"
        />
      )}
      {!!updateManualTotalMutation && (
        <ManualTotalDrawerForm
          description="Modifiez les valeurs utilisées pour le calcul manuel du total."
          initialValue={
            initialValueFromManualTotal ?? initialValueFromAutomaticResult
          }
          isOpen={formVariant === 'edit'}
          onClose={closeForm}
          onSubmit={handleUpdateSubmit}
          resolvableFieldSettings={resolvableFieldSettings}
          submitLabel="Enregistrer"
          title="Modifier les valeurs du total manuel"
        />
      )}
    </>
  );
};
